/* ============================================================================
   ASTROLOOT — Motion & interaction core
   Zero dependencies. Deferred. ~7KB unminified.
   Everything degrades gracefully: no JS = static, readable, fully shoppable.
   ========================================================================= */
(function () {
  'use strict';

  var root = document.documentElement;
  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var coarse = window.matchMedia('(pointer: coarse)').matches;
  var motionOff = root.classList.contains('motion-off');
  var lowMotion = reduced || motionOff;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- 1. Scroll reveal ------------------------------------------ */

  function initReveal() {
    var els = document.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    if (lowMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-revealed'); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var delay = parseInt(el.getAttribute('data-reveal-delay') || '0', 10);
        el.style.setProperty('--reveal-delay', delay + 'ms');
        el.classList.add('is-revealed');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    els.forEach(function (el) { io.observe(el); });

    /* Auto-stagger children of a [data-stagger] parent */
    document.querySelectorAll('[data-stagger]').forEach(function (parent) {
      var step = parseInt(parent.getAttribute('data-stagger') || '80', 10);
      var kids = parent.querySelectorAll(':scope > [data-reveal]');
      kids.forEach(function (kid, i) {
        var base = parseInt(kid.getAttribute('data-reveal-delay') || '0', 10);
        kid.setAttribute('data-reveal-delay', String(base + i * step));
      });
    });
  }

  /* ---------- 2. Split-word headline reveal ----------------------------- */

  function initSplit() {
    var els = document.querySelectorAll('[data-split]');
    if (!els.length || lowMotion) return;

    els.forEach(function (el) {
      if (el.dataset.splitDone) return;
      var lines = el.innerHTML.split(/<br\s*\/?>/i);
      var idx = 0;
      var html = lines.map(function (line) {
        var words = line.trim().split(/\s+/).filter(Boolean);
        return '<span class="split-line">' + words.map(function (w) {
          var span = '<span class="split-word" style="--word-delay:' + (idx * 55) + 'ms">' + w + '</span>';
          idx++;
          return span;
        }).join(' ') + '</span>';
      }).join('');
      el.innerHTML = html;
      el.dataset.splitDone = '1';
    });
  }

  /* ---------- 3. Pointer parallax (--mx / --my) ------------------------- */

  function initPointer() {
    if (lowMotion || coarse) return;
    var tx = 0, ty = 0, cx = 0, cy = 0, raf = null;

    window.addEventListener('pointermove', function (e) {
      tx = (e.clientX / window.innerWidth) * 2 - 1;
      ty = (e.clientY / window.innerHeight) * 2 - 1;
      if (!raf) raf = requestAnimationFrame(tick);
    }, { passive: true });

    function tick() {
      cx += (tx - cx) * 0.075;
      cy += (ty - cy) * 0.075;
      root.style.setProperty('--mx', cx.toFixed(4));
      root.style.setProperty('--my', cy.toFixed(4));
      if (Math.abs(tx - cx) > 0.001 || Math.abs(ty - cy) > 0.001) {
        raf = requestAnimationFrame(tick);
      } else {
        raf = null;
      }
    }
  }

  /* ---------- 4. Cursor-tracked card highlight -------------------------- */

  function initCardGlow() {
    if (coarse) return;
    document.addEventListener('pointermove', function (e) {
      var card = e.target.closest && e.target.closest('.itile, .glass--hover, [data-glow]');
      if (!card) return;
      var r = card.getBoundingClientRect();
      card.style.setProperty('--px', ((e.clientX - r.left) / r.width * 100).toFixed(2) + '%');
      card.style.setProperty('--py', ((e.clientY - r.top) / r.height * 100).toFixed(2) + '%');
    }, { passive: true });
  }

  /* ---------- 5. Scroll-linked depth parallax --------------------------- */

  function initScrollParallax() {
    var els = document.querySelectorAll('[data-parallax]');
    if (!els.length || lowMotion) return;
    var ticking = false;

    function update() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var speed = parseFloat(el.getAttribute('data-parallax')) || 0.12;
        var progress = (r.top + r.height / 2 - vh / 2) / vh;
        el.style.setProperty('--scroll-y', (progress * speed * -220).toFixed(2) + 'px');
      });
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ---------- 6. Header scroll state ------------------------------------ */

  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (!header) return;
    var last = 0, ticking = false;

    function measure() {
      root.style.setProperty('--header-h', header.offsetHeight + 'px');
    }
    measure();
    window.addEventListener('resize', measure, { passive: true });

    function update() {
      var y = window.scrollY;
      header.classList.toggle('is-stuck', y > 24);
      /* Hide on scroll down past the fold, reveal on scroll up */
      if (y > 420 && y > last + 6) header.classList.add('is-hidden');
      else if (y < last - 6 || y < 200) header.classList.remove('is-hidden');
      last = y;
      ticking = false;
    }

    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
  }

  /* ---------- 7. Mobile nav / drawers ----------------------------------- */

  function lockScroll(on) {
    document.body.style.overflow = on ? 'hidden' : '';
  }

  function initDrawers() {
    document.addEventListener('click', function (e) {
      var opener = e.target.closest('[data-drawer-open]');
      if (opener) {
        var d = document.getElementById(opener.getAttribute('data-drawer-open'));
        if (d) { openDrawer(d, opener); e.preventDefault(); }
        return;
      }
      var closer = e.target.closest('[data-drawer-close]');
      if (closer) {
        var dc = closer.closest('[data-drawer]');
        if (dc) { closeDrawer(dc); e.preventDefault(); }
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelector('[data-drawer].is-open');
      if (open) closeDrawer(open);
    });
  }

  function openDrawer(d, trigger) {
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    d._trigger = trigger || null;
    lockScroll(true);
    var focusable = d.querySelector('[data-drawer-close], a, button, input');
    if (focusable) focusable.focus();
  }

  function closeDrawer(d) {
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    lockScroll(false);
    if (d._trigger) d._trigger.focus();
  }

  /* ---------- 8. Marquee: duplicate track for a seamless loop ----------- */

  function initMarquee() {
    document.querySelectorAll('.marquee__track').forEach(function (track) {
      if (track.dataset.cloned) return;
      track.innerHTML += track.innerHTML;
      track.dataset.cloned = '1';
    });
  }

  /* ---------- 9. Horizontal rails --------------------------------------- */

  function initRails() {
    document.querySelectorAll('[data-rail]').forEach(function (wrap) {
      var rail = wrap.querySelector('.rail');
      var prev = wrap.querySelector('[data-rail-prev]');
      var next = wrap.querySelector('[data-rail-next]');
      if (!rail) return;

      function step() {
        var first = rail.querySelector(':scope > *');
        return first ? first.getBoundingClientRect().width + 20 : rail.clientWidth * 0.8;
      }
      function sync() {
        if (prev) prev.disabled = rail.scrollLeft < 8;
        if (next) next.disabled = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 8;
      }
      if (prev) prev.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
      rail.addEventListener('scroll', function () { requestAnimationFrame(sync); }, { passive: true });
      sync();
    });
  }

  /* ---------- 10. Testimonial slider ------------------------------------ */

  function initSlider() {
    document.querySelectorAll('[data-slider]').forEach(function (el) {
      var slides = Array.prototype.slice.call(el.querySelectorAll('[data-slide]'));
      if (slides.length < 2) return;
      var dots = el.querySelector('[data-slider-dots]');
      var i = 0, timer = null;
      var auto = parseInt(el.getAttribute('data-slider') || '0', 10);

      function go(n) {
        i = (n + slides.length) % slides.length;
        slides.forEach(function (s, k) {
          var active = k === i;
          s.classList.toggle('is-active', active);
          s.setAttribute('aria-hidden', active ? 'false' : 'true');
        });
        if (dots) {
          Array.prototype.forEach.call(dots.children, function (d, k) {
            d.classList.toggle('is-active', k === i);
            d.setAttribute('aria-selected', k === i ? 'true' : 'false');
          });
        }
      }

      if (dots) {
        dots.innerHTML = '';
        slides.forEach(function (_, k) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'slider-dot';
          b.setAttribute('role', 'tab');
          b.setAttribute('aria-label', 'Review ' + (k + 1));
          b.addEventListener('click', function () { go(k); restart(); });
          dots.appendChild(b);
        });
      }

      var p = el.querySelector('[data-slider-prev]');
      var n = el.querySelector('[data-slider-next]');
      if (p) p.addEventListener('click', function () { go(i - 1); restart(); });
      if (n) n.addEventListener('click', function () { go(i + 1); restart(); });

      function restart() {
        if (timer) clearInterval(timer);
        if (auto > 0 && !lowMotion) timer = setInterval(function () { go(i + 1); }, auto);
      }

      el.addEventListener('mouseenter', function () { if (timer) clearInterval(timer); });
      el.addEventListener('mouseleave', restart);

      go(0);
      restart();
    });
  }

  /* ---------- 11. Product gallery + zoom -------------------------------- */

  function initGallery() {
    document.querySelectorAll('[data-gallery]').forEach(function (g) {
      var stage = g.querySelector('[data-gallery-stage]');
      var thumbs = g.querySelectorAll('[data-gallery-thumb]');
      if (!stage) return;

      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          var id = t.getAttribute('data-gallery-thumb');
          g.querySelectorAll('[data-gallery-slide]').forEach(function (s) {
            s.classList.toggle('is-active', s.getAttribute('data-gallery-slide') === id);
          });
          thumbs.forEach(function (x) {
            x.classList.toggle('is-active', x === t);
            x.setAttribute('aria-selected', x === t ? 'true' : 'false');
          });
        });
      });

      /* Hover-to-zoom on fine pointers only */
      if (coarse) return;
      stage.addEventListener('pointermove', function (e) {
        var img = e.target.closest('.pdp__img');
        if (!img) return;
        var r = img.getBoundingClientRect();
        img.style.transformOrigin =
          ((e.clientX - r.left) / r.width * 100).toFixed(2) + '% ' +
          ((e.clientY - r.top) / r.height * 100).toFixed(2) + '%';
      }, { passive: true });
    });
  }

  /* ---------- 12. Quantity steppers ------------------------------------- */

  function initQty() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty]');
      if (!btn) return;
      var wrap = btn.closest('[data-qty-wrap]');
      var input = wrap && wrap.querySelector('input[type="number"]');
      if (!input) return;
      var min = parseInt(input.min || '1', 10);
      var v = parseInt(input.value || '1', 10) + (btn.getAttribute('data-qty') === 'up' ? 1 : -1);
      input.value = String(Math.max(min, v));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  /* ---------- 13. Variant selection -------------------------------------- */

  function initVariants() {
    document.querySelectorAll('[data-variants]').forEach(function (form) {
      var data = form.querySelector('[data-variant-json]');
      if (!data) return;
      var variants;
      try { variants = JSON.parse(data.textContent); } catch (err) { return; }

      var idInput = form.querySelector('[name="id"]');
      var priceEl = form.querySelector('[data-variant-price]');
      var compareEl = form.querySelector('[data-variant-compare]');
      var btn = form.querySelector('[data-add-btn]');
      var money = form.getAttribute('data-money-format') || '${{amount}}';

      function fmt(cents) {
        var v = (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return money.replace(/\{\{\s*amount\s*\}\}/, v)
                    .replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(cents / 100)))
                    .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, v);
      }

      function update() {
        var chosen = Array.prototype.map.call(
          form.querySelectorAll('[data-option-index]'),
          function (input) {
            if (input.type === 'radio') return null;
            return input.value;
          }
        );
        /* Radio groups: read the checked one per option index */
        var opts = [];
        form.querySelectorAll('[data-option-group]').forEach(function (group) {
          var checked = group.querySelector('input:checked');
          opts.push(checked ? checked.value : null);
        });
        if (!opts.length) opts = chosen;

        var match = variants.filter(function (v) {
          return opts.every(function (o, k) { return o === null || v.options[k] === o; });
        })[0];

        if (!match) {
          if (btn) { btn.disabled = true; btn.textContent = btn.getAttribute('data-unavailable') || 'Unavailable'; }
          return;
        }
        if (idInput) idInput.value = match.id;
        if (priceEl) priceEl.textContent = fmt(match.price);
        if (compareEl) {
          if (match.compare_at_price && match.compare_at_price > match.price) {
            compareEl.textContent = fmt(match.compare_at_price);
            compareEl.hidden = false;
          } else { compareEl.hidden = true; }
        }
        if (btn) {
          btn.disabled = !match.available;
          btn.textContent = match.available
            ? (btn.getAttribute('data-add') || 'Add to cart')
            : (btn.getAttribute('data-soldout') || 'Sold out');
        }
        if (history.replaceState && match.id) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', match.id);
          history.replaceState({}, '', url);
        }
      }

      form.addEventListener('change', update);
      update();
    });
  }

  /* ---------- 14. Add to cart (AJAX) + cart count ------------------------ */

  function initCart() {
    var routes = window.AstroRoutes || {};
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[data-ajax-cart]');
      if (!form || !routes.cartAdd) return;
      e.preventDefault();
      var btn = form.querySelector('[data-add-btn]');
      var original = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = btn.getAttribute('data-adding') || 'Adding…'; }

      fetch(routes.cartAdd, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: new FormData(form)
      })
        .then(function (r) { return r.json().then(function (j) { return { ok: r.ok, body: j }; }); })
        .then(function (res) {
          if (!res.ok) throw new Error(res.body.description || 'Could not add to cart');
          if (btn) btn.textContent = btn.getAttribute('data-added') || 'Added ✓';
          refreshCount();
          var drawer = document.getElementById('cart-drawer');
          if (drawer) { refreshDrawer().then(function () { openDrawer(drawer, btn); }); }
        })
        .catch(function (err) {
          var msg = form.querySelector('[data-cart-error]');
          if (msg) { msg.textContent = err.message; msg.hidden = false; }
          if (btn) btn.textContent = original;
        })
        .finally(function () {
          setTimeout(function () {
            if (btn) { btn.disabled = false; btn.textContent = original; }
          }, 1600);
        });
    });

    function refreshCount() {
      if (!routes.cart) return Promise.resolve();
      return fetch(routes.cart + '.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.querySelectorAll('[data-cart-count]').forEach(function (el) {
            el.textContent = cart.item_count;
            el.classList.toggle('is-empty', cart.item_count === 0);
          });
        }).catch(function () {});
    }

    function refreshDrawer() {
      var drawer = document.getElementById('cart-drawer');
      if (!drawer || !routes.cart) return Promise.resolve();
      return fetch(routes.cart + '?section_id=cart-drawer')
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-cart-body]');
          var target = drawer.querySelector('[data-cart-body]');
          if (fresh && target) target.innerHTML = fresh.innerHTML;
        }).catch(function () {});
    }

    window.AstroCart = { refreshCount: refreshCount, refreshDrawer: refreshDrawer };
  }

  /* ---------- 15. Count-up stats ---------------------------------------- */

  function initCounters() {
    var els = document.querySelectorAll('[data-count-to]');
    if (!els.length || !('IntersectionObserver' in window)) return;
    if (lowMotion) {
      els.forEach(function (el) { el.textContent = el.getAttribute('data-count-to'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        var target = parseFloat(el.getAttribute('data-count-to')) || 0;
        var dur = 1500, start = performance.now();
        (function frame(now) {
          var p = Math.min((now - start) / dur, 1);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased).toLocaleString();
          if (p < 1) requestAnimationFrame(frame);
        })(start);
        io.unobserve(el);
      });
    }, { threshold: 0.5 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 16. Autoplaying video: only while on screen ---------------- */

  function initVideo() {
    var vids = document.querySelectorAll('video[data-autopause]');
    if (!vids.length) return;

    if (lowMotion) {
      vids.forEach(function (v) { v.removeAttribute('autoplay'); v.pause(); v.controls = true; });
      return;
    }
    if (!('IntersectionObserver' in window)) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var v = entry.target;
        if (entry.isIntersecting) {
          var p = v.play();
          if (p && p.catch) p.catch(function () { v.controls = true; });
        } else {
          v.pause();
        }
      });
    }, { threshold: 0.2 });

    vids.forEach(function (v) { io.observe(v); });

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) return;
      vids.forEach(function (v) { v.pause(); });
    });
  }

  /* ---------- 16b. Consent gates ---------------------------------------- */

  function initConsent() {
    var wraps = document.querySelectorAll('[data-consent]');
    if (!wraps.length) return;

    Array.prototype.forEach.call(wraps, function (wrap) {
      if (wrap.dataset.consentBound) return;
      wrap.dataset.consentBound = '1';

      var box = wrap.querySelector('[data-consent-input]');
      var err = wrap.querySelector('[data-consent-err]');
      if (!box) return;

      var form = box.form;
      var sel = wrap.getAttribute('data-consent-gate');
      var btn = sel && form ? form.querySelector(sel) : null;

      function paint() {
        var ok = box.checked;
        wrap.classList.toggle('consent--invalid', !ok && wrap.dataset.tried === '1');
        if (err) err.hidden = ok || wrap.dataset.tried !== '1';
        if (btn) {
          /* aria-disabled, not disabled: a disabled submit is skipped by some
             browsers when the form is submitted another way, and it drops the
             button out of the tab order entirely. */
          btn.setAttribute('aria-disabled', ok ? 'false' : 'true');
        }
      }

      box.addEventListener('change', function () {
        if (box.checked) wrap.dataset.tried = '';
        paint();
      });

      if (btn) {
        btn.addEventListener('click', function (e) {
          if (box.checked) return;
          e.preventDefault();
          wrap.dataset.tried = '1';
          paint();
          try { box.focus({ preventScroll: false }); } catch (_) { box.focus(); }
        });
      }

      if (form) {
        form.addEventListener('submit', function (e) {
          /* Only gate the submitter we were asked to gate. "Update cart" and
             quantity changes must never be blocked by the terms box. */
          var by = e.submitter;
          if (btn && by && by !== btn) return;
          if (!btn && by && by.type !== 'submit') return;
          if (box.checked) return;
          e.preventDefault();
          wrap.dataset.tried = '1';
          paint();
          box.focus();
        });
      }

      paint();
    });
  }

  /* ---------- 16c. Inventory catalog: tabs, search, sort ----------------- */

  function initInventory() {
    var root = document.querySelector('[data-inv-grid]');
    if (!root || root.dataset.invBound) return;
    root.dataset.invBound = '1';

    var items   = Array.prototype.slice.call(root.querySelectorAll('[data-inv-item]'));
    var tabs    = document.querySelectorAll('[data-inv-tab]');
    var search  = document.querySelector('[data-inv-search]');
    var sortSel = document.querySelector('[data-inv-sort]');
    var countEl = document.querySelector('[data-inv-count]');
    var emptyEl = document.querySelector('[data-inv-empty]');
    var resetEl = document.querySelector('[data-inv-reset]');

    var tab = 'all';
    var q = '';

    /* Cheap searchable haystack per card, built once. */
    items.forEach(function (el) {
      el._hay = [el.dataset.name, el.dataset.set, el.dataset.sku]
        .join(' ').toLowerCase();
      el._price = parseFloat(el.dataset.price) || 0;
      el._added = parseInt(el.dataset.added, 10) || 0;
      el._name = (el.dataset.name || '').toLowerCase();
      el._class = el.dataset.class || 'raw';
    });

    function matchesTab(el) {
      if (tab === 'all' || tab === 'recent') return true;
      return el._class === tab;
    }

    function apply() {
      var shown = [];

      items.forEach(function (el) {
        var ok = matchesTab(el) && (!q || el._hay.indexOf(q) !== -1);
        el.hidden = !ok;
        if (ok) shown.push(el);
      });

      /* "Recently Added" is a view, not a category: it forces the sort. */
      var mode = tab === 'recent' ? 'recent' : (sortSel ? sortSel.value : 'recent');

      shown.sort(function (a, b) {
        switch (mode) {
          case 'price-desc': return b._price - a._price;
          case 'price-asc':  return a._price - b._price;
          case 'name':       return a._name < b._name ? -1 : a._name > b._name ? 1 : 0;
          default:           return b._added - a._added;
        }
      });

      /* Availability outranks whatever the sort dropdown says. Real inventory a
         customer can actually buy leads the grid; sold sinks. Applied last, so
         it is the primary key and the chosen sort orders within each band.
           0  in stock now  (passed the full purchase gate)
           1  everything else — out of stock, reserved, demo samples
           2  sold */
      function availRank(el) {
        if (el.dataset.status === 'sold') return 2;
        if (el.dataset.instock === 'true') return 0;
        return 1;
      }
      shown.sort(function (a, b) {
        return availRank(a) - availRank(b);
      });

      var frag = document.createDocumentFragment();
      shown.forEach(function (el, i) {
        el.style.setProperty('--ic-i', i);
        frag.appendChild(el);
      });
      root.appendChild(frag);

      if (countEl) {
        countEl.textContent = shown.length
          ? shown.length + (shown.length === 1 ? ' item' : ' items')
          : '';
      }
      if (emptyEl) emptyEl.hidden = shown.length !== 0;
    }

    /* Per-tab counts, computed once from the real DOM. */
    function paintTabCounts() {
      var n = { all: items.length, raw: 0, slab: 0, sealed: 0, recent: items.length };
      items.forEach(function (el) {
        if (n[el._class] !== undefined) n[el._class]++;
      });
      Object.keys(n).forEach(function (k) {
        var el = document.querySelector('[data-inv-tabcount="' + k + '"]');
        if (el) el.textContent = n[k];
      });
    }

    Array.prototype.forEach.call(tabs, function (btn) {
      btn.addEventListener('click', function () {
        tab = btn.getAttribute('data-inv-tab');
        Array.prototype.forEach.call(tabs, function (b) {
          var on = b === btn;
          b.classList.toggle('is-active', on);
          b.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        if (sortSel) sortSel.disabled = (tab === 'recent');
        apply();
      });
    });

    if (search) {
      var t;
      search.addEventListener('input', function () {
        clearTimeout(t);
        t = setTimeout(function () { q = search.value.trim().toLowerCase(); apply(); }, 120);
      });
    }

    if (sortSel) sortSel.addEventListener('change', apply);

    if (resetEl) {
      resetEl.addEventListener('click', function () {
        q = '';
        if (search) search.value = '';
        var allTab = document.querySelector('[data-inv-tab="all"]');
        if (allTab) allTab.click(); else apply();
      });
    }

    paintTabCounts();
    apply();
  }

  /* ---------- 16d. Inventory item detail panel --------------------------- */

  function initInvPanel() {
    var panel = document.querySelector('[data-inv-panel]');
    if (!panel || panel.dataset.bound) return;
    panel.dataset.bound = '1';

    var grid = document.querySelector('[data-inv-grid]');
    if (!grid) return;

    var q = function (s) { return panel.querySelector(s); };
    var lastFocus = null;

    function fill(card) {
      var d = card.dataset;
      var fig = card.querySelector('.ai');

      q('[data-ivd-media]').innerHTML = fig ? fig.outerHTML : '';
      var m = q('[data-ivd-media] .ai__img');
      if (m) { m.removeAttribute('loading'); m.removeAttribute('sizes'); }

      q('[data-ivd-class]').textContent = (card.querySelector('.ic__class') || {}).textContent || '';

      /* Demo badge in the drawer, mirroring the badge on the card. */
      var demoTag = panel.querySelector('.ivd__demo');
      if (d.demo === 'true') {
        if (!demoTag) {
          demoTag = document.createElement('span');
          demoTag.className = 'ivd__demo';
          demoTag.textContent = 'Demo listing';
          var nameEl = q('[data-ivd-name]');
          nameEl.parentNode.insertBefore(demoTag, nameEl);
        }
      } else if (demoTag) {
        demoTag.remove();
      }
      q('[data-ivd-name]').textContent  = d.name || '';
      q('[data-ivd-meta]').textContent  = (card.querySelector('.ic__meta') || {}).textContent || '';
      var priceEl = card.querySelector('.ic__price');
      var priceTxt = priceEl ? (priceEl.firstChild ? priceEl.firstChild.textContent : priceEl.textContent) : '';
      q('[data-ivd-price]').textContent = (priceTxt || '').trim() + (d.demo === 'true' ? ' — sample value' : '');
      q('[data-ivd-sku]').textContent   = d.sku || '';

      /* Spec list is built from what the item actually has — no empty rows. */
      var spec = q('[data-ivd-spec]');
      spec.innerHTML = '';
      var chips = card.querySelectorAll('.ic__chip');
      var rows = [];
      if (d.set) rows.push(['Set', d.set]);
      var grade = card.querySelector('.ic__chip--grade');
      if (grade) rows.push(['Grade', grade.textContent.trim()]);
      else if (chips.length > 1) rows.push(['Condition', chips[0].textContent.trim()]);
      var tier = fig ? fig.getAttribute('data-image-tier') : '';
      if (tier === 'actual') rows.push(['Image', 'Photograph of this item']);
      else if (tier === 'standin') {
        /* An AstroLoot-drawn graphic. Say so plainly — it is neither our
           photograph nor the card's real artwork. */
        rows.push(['Image', 'AstroLoot demo graphic, not the card']);
      }
      else if (tier === 'illustration') {
        /* Name the source. "Illustration" alone reads like a hedge; saying where
           the art came from is what makes the provenance claim checkable. */
        var cid = d.cardId || '';
        rows.push(['Image', cid.indexOf('tcgdex:') === 0
          ? 'Canonical card art, not this copy'
          : 'Illustration, not this copy']);
        if (cid) rows.push(['Printing', cid.replace(/^[a-z]+:/, '')]);
      }
      /* Never print "Available" for a demo record — that is the exact claim the
         launch safeguard exists to prevent. */
      rows.push(['Availability', d.demo === 'true'
        ? 'Demo listing — not available for purchase'
        : d.status === 'sold' ? 'Sold' : d.status === 'reserved' ? 'Reserved' : 'Available']);
      rows.forEach(function (r) {
        var dt = document.createElement('dt'); dt.textContent = r[0];
        var dd = document.createElement('dd'); dd.textContent = r[1];
        spec.appendChild(dt); spec.appendChild(dd);
      });

      /* CTA must never lie about what happens next.

         LAUNCH SAFEGUARD: a demo record gets a non-interactive label, never a
         link or an enabled control. data-demo is written server-side by the
         eligibility gate in inventory-card.liquid, so the drawer cannot
         disagree with the card it was opened from. */
      var cta = q('[data-ivd-cta]');
      var label = panel.getAttribute('data-enquire-label') || 'Enquire about this item';
      var url = panel.getAttribute('data-enquire-url') || '/pages/contact';
      var isDemo = d.demo === 'true';

      if (isDemo) {
        cta.innerHTML =
          '<button class="btn btn--ghost btn--lg" type="button" disabled aria-disabled="true">' +
          'Preview listing</button>' +
          '<p class="ivd__notice">Demo listing — not available for purchase. ' +
          'Pricing shown is a sample value, not an offer.</p>';
      } else if (d.status === 'sold') {
        cta.innerHTML = '<button class="btn btn--ghost btn--lg" type="button" disabled>Sold</button>';
      } else {
        cta.innerHTML = '<a class="btn btn--primary btn--lg" href="' + url + '">' + label + '</a>';
      }
    }

    function open(card) {
      lastFocus = document.activeElement;
      fill(card);
      panel.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      document.body.classList.add('ivd-open');
      var x = panel.querySelector('.ivd__x');
      if (x) x.focus();
    }

    function close() {
      panel.hidden = true;
      panel.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('ivd-open');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    grid.addEventListener('click', function (e) {
      var card = e.target.closest('[data-inv-item]');
      /* Real products are <a> elements — let the browser navigate. */
      if (!card || card.tagName === 'A') return;
      open(card);
    });

    grid.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter' && e.key !== ' ') return;
      var card = e.target.closest('[data-inv-item]');
      if (!card || card.tagName === 'A') return;
      e.preventDefault();
      open(card);
    });

    Array.prototype.forEach.call(panel.querySelectorAll('[data-inv-close]'), function (b) {
      b.addEventListener('click', close);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !panel.hidden) close();
    });

    /* Keyboard reachability for non-link cards. */
    Array.prototype.forEach.call(grid.querySelectorAll('[data-inv-item]:not(a)'), function (c) {
      if (!c.hasAttribute('tabindex')) c.setAttribute('tabindex', '0');
      if (!c.hasAttribute('role')) c.setAttribute('role', 'button');
    });
  }

  /* ---------- 16e. Tier deep-link (?tier=G-04) ---------------------------- */

  function initTierDeepLink() {
    var radios = document.querySelectorAll('.gt__radio');
    if (!radios.length) return;

    var code = null;
    try {
      code = new URLSearchParams(location.search).get('tier');
    } catch (_) { return; }
    if (!code) return;

    code = code.trim().toUpperCase();
    var hit = null;
    Array.prototype.forEach.call(radios, function (r) {
      if (!hit && (r.value || '').trim().toUpperCase() === code) hit = r;
    });
    if (!hit) return;

    /* Panels switch via CSS :has() on the checked radio, so setting .checked is
       all that is needed — no state to sync, nothing to re-render. */
    hit.checked = true;
    hit.dispatchEvent(new Event('change', { bubbles: true }));

    /* Land the customer on the selector rather than the top of the page. */
    var sec = hit.closest('.gt');
    if (sec && !location.hash) {
      requestAnimationFrame(function () {
        sec.scrollIntoView({ behavior: lowMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }
  }

  /* ---------- 17. Boot --------------------------------------------------- */

  ready(function () {
    initSplit();
    initReveal();
    initPointer();
    initCardGlow();
    initScrollParallax();
    initHeader();
    initDrawers();
    initMarquee();
    initRails();
    initSlider();
    initGallery();
    initQty();
    initVariants();
    initCart();
    initCounters();
    initVideo();
    initConsent();
    initInventory();
    initInvPanel();
    initTierDeepLink();

    /* Re-init when the theme editor swaps a section */
    document.addEventListener('shopify:section:load', function () {
      initSplit(); initReveal(); initMarquee(); initRails();
      initSlider(); initGallery(); initVariants(); initCounters(); initVideo();
      initConsent(); initInventory(); initInvPanel(); initTierDeepLink();
    });
  });
})();
